import axios from 'axios';

const api = axios.create({
    withCredentials: true,
    baseURL: '/api/cognitive-flows',
});

/**
 * Point API at the ScaleX backend (e.g. http://localhost:9000/api/cognitive-flows).
 */
export function setCognitiveFlowsApiBase(appUrl) {
    const base = String(appUrl || '').replace(/\/$/, '');
    api.defaults.baseURL = `${base}/api/cognitive-flows`;
}

// Selected workspace scope. Every CF request carries it as the x-cf-workspace
// header ('account' = account level); the server validates access per request.
let currentWorkspaceId = null;

export function setCognitiveFlowsWorkspace(workspaceId) {
    currentWorkspaceId = workspaceId || null;
}

// PM QA scope: while an admin has another account's canvas open inside the QA
// portal, every CF request carries `x-cf-qa-shop` (honored server-side for
// platform admins only — requireShop) and the WORKSPACE header is forced to
// the QA canvas's own workspace: the admin's selected workspace belongs to
// their account and would 404 resolveWorkspace against the QA shop.
// Module state like the workspace above — set on the QA route's mount, cleared
// on unmount (PmQaCanvasPage).
let qaScope = null; // { shop, workspaceId } | null

export function setCognitiveFlowsQaScope(scope) {
    qaScope = scope?.shop ? { shop: scope.shop, workspaceId: scope.workspaceId || null } : null;
}

export function getCognitiveFlowsQaShop() {
    return qaScope?.shop || null;
}

/** For raw fetch() calls (SSE streams) that bypass the axios interceptor. */
export function getCognitiveFlowsWorkspace() {
    if (qaScope) return qaScope.workspaceId || 'account';
    return currentWorkspaceId || 'account';
}

api.interceptors.request.use((config) => {
    if (qaScope) {
        config.headers['x-cf-qa-shop'] = qaScope.shop;
        // QA mode overrides even caller-set workspace headers: any explicit id
        // from the surrounding app belongs to the ADMIN's account, not the QA
        // shop, and would 404 there.
        config.headers['x-cf-workspace'] = qaScope.workspaceId || 'account';
        return config;
    }
    // A caller that set the header ITSELF is targeting a specific workspace
    // rather than the selected one — the workspace form edits a workspace from
    // a list, which is often not the one currently in scope, and silently
    // rewriting it here would save the wrong workspace's settings under the
    // right workspace's name. The server still validates the id against this
    // account (resolveWorkspace), so an explicit value is no less safe.
    if (!config.headers['x-cf-workspace']) {
        config.headers['x-cf-workspace'] = currentWorkspaceId || 'account';
    }
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

export default api;
